import api from './api';

// Types
export interface VoucherTipo {
  id: number;
  nome: string;
  tipo: 'valor' | 'desconto';
  icone: string;
  cor_fundo: string;
  cor_texto: string;
}

export interface Voucher {
  id: number;
  codigo: string;
  titulo: string;
  descricao: string;
  valor: number | null;
  percentagem_desconto: number | null;
  preco_venda: number;
  data_validade: string | null;
  imagem_logo: string | null;
  disponivel: number;
  tipo_nome: string;
  tipo_categoria: 'valor' | 'desconto';
  icone: string;
  cor_fundo: string;
  cor_texto: string;
  empresa_nome: string;
  empresa_logo: string | null;
}

export interface VoucherDetalhes extends Voucher {
  empresa_endereco: string | null;
  empresa_telefone: string | null;
  termos_uso: string | null;
  valor_minimo_compra: number | null;
}

export interface VoucherPagamento {
  referencia: string;
  valor: number;
  saldo_anterior: number;
  saldo_posterior: number;
}

export interface VoucherCompra {
  id: number;
  voucher_id: number;
  codigo_unico: string;
  qr_code_data: string;
  beneficiario_nome: string;
  valor_restante: number | null;
  status: 'ativo' | 'parcialmente_usado' | 'usado' | 'expirado' | 'cancelado';
  data_compra: string;
  data_uso: string | null;
  titulo: string;
  descricao: string;
  preco_venda: number;
  data_validade: string | null;
  valor: number | null;
  percentagem_desconto: number | null;
  imagem_logo: string | null;
  tipo_nome: string;
  tipo_categoria: 'valor' | 'desconto';
  icone: string;
  cor_fundo: string;
  cor_texto: string;
  empresa_nome: string;
  empresa_logo: string | null;
  empresa_endereco: string | null;
  resgates?: VoucherResgate[];
  pagamento?: VoucherPagamento;
}

export interface VoucherResgate {
  id: number;
  compra_id: number;
  valor_resgatado: number | null;
  desconto_aplicado: number | null;
  empresa_nome: string;
  created_at: string;
}

// ============================================
// PORTAL (BUSINESS) TYPES
// ============================================

export interface PortalVoucher {
  id: number;
  codigo: string;
  tipo_id: number;
  tipo_nome: string;
  tipo_categoria: 'valor' | 'desconto';
  empresa_id: number;
  titulo: string;
  descricao: string | null;
  valor: number | null;
  percentagem_desconto: number | null;
  valor_minimo_compra: number | null;
  quantidade_total: number;
  quantidade_vendida: number;
  preco_venda: number;
  data_validade: string | null;
  termos_uso: string | null;
  imagem_logo: string | null;
  status: 'rascunho' | 'ativo' | 'esgotado' | 'expirado' | 'cancelado';
  total_vendas: number;
  receita_total: number;
  created_at: string;
}

export interface PortalVoucherInput {
  tipo_id: number;
  titulo: string;
  descricao?: string | null;
  valor?: number | null;
  percentagem_desconto?: number | null;
  valor_minimo_compra?: number | null;
  quantidade_total: number;
  preco_venda: number;
  data_validade?: string | null;
  termos_uso?: string | null;
  imagem_logo?: string | null;
  status?: 'rascunho' | 'ativo' | 'esgotado' | 'expirado' | 'cancelado';
}

export interface PortalVoucherStats {
  total_vouchers: number;
  vouchers_ativos: number;
  total_vendas: number;
  receita_total: number;
  total_resgates: number;
}

export interface PortalVoucherVenda {
  id: number;
  voucher_id: number;
  user_id: number;
  codigo_unico: string;
  beneficiario_nome: string | null;
  comprador_nome: string | null;
  valor_restante: number | null;
  preco_pago: number;
  status: string;
  data_compra: string;
  data_uso: string | null;
}

export interface PortalResgateHistorico {
  id: number;
  compra_id: number;
  codigo_unico: string;
  voucher_titulo: string;
  beneficiario_nome: string | null;
  operador_nome: string | null;
  valor_resgatado: number | null;
  desconto_aplicado: number | null;
  metodo_validacao: 'qr_code' | 'codigo_manual';
  localizacao: string | null;
  notas: string | null;
  created_at: string;
}

export interface VoucherValidacao {
  id: number;
  codigo_unico: string;
  titulo: string;
  valor: number | null;
  valor_restante: number | null;
  percentagem_desconto: number | null;
  tipo_categoria: 'valor' | 'desconto';
  beneficiario_nome: string | null;
  data_validade: string | null;
  status: string;
  valido: boolean;
  mensagem: string;
}

class VouchersService {
  // ============================================
  // STORE (PUBLIC)
  // ============================================

  async getVouchersStore(params?: {
    categoria?: string;
    empresa_id?: number;
    tipo?: string;
    limit?: number;
    offset?: number;
  }): Promise<Voucher[]> {
    const searchParams = new URLSearchParams();
    if (params?.categoria) searchParams.append('categoria', params.categoria);
    if (params?.empresa_id) searchParams.append('empresa_id', params.empresa_id.toString());
    if (params?.tipo) searchParams.append('tipo', params.tipo);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const query = searchParams.toString();
    const url = `/vouchers/store${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: Voucher[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async getVoucherDetalhes(id: number): Promise<VoucherDetalhes> {
    const response = await api.get<{
      success: boolean;
      data?: VoucherDetalhes;
      error?: string;
    }>(`/vouchers/store/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes do voucher');
  }

  async getVouchersDestaque(): Promise<Voucher[]> {
    const response = await api.get<{
      success: boolean;
      data?: Voucher[];
      error?: string;
    }>('/vouchers/destaque');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async getCategorias(): Promise<VoucherTipo[]> {
    const response = await api.get<{
      success: boolean;
      data?: VoucherTipo[];
      error?: string;
    }>('/vouchers/categorias');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  // ============================================
  // PURCHASES (AUTHENTICATED)
  // ============================================

  async comprarVoucher(
    voucherId: number,
    dados?: {
      beneficiario_nome?: string;
      beneficiario_email?: string;
      como_presente?: boolean;
      pin?: string;
    }
  ): Promise<VoucherCompra> {
    const response = await api.post<{
      success: boolean;
      data?: VoucherCompra & { pagamento?: VoucherPagamento };
      error?: string;
    }>(`/client/vouchers/${voucherId}/comprar`, dados || {});

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao comprar voucher');
  }

  async getMeusVouchers(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<VoucherCompra[]> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const query = searchParams.toString();
    const url = `/client/vouchers/meus${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: VoucherCompra[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async getMeuVoucherDetalhes(compraId: number): Promise<VoucherCompra> {
    const response = await api.get<{
      success: boolean;
      data?: VoucherCompra;
      error?: string;
    }>(`/client/vouchers/meus/${compraId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes do voucher');
  }

  async transferirVoucher(
    compraId: number,
    dados: {
      destinatario_email?: string;
      destinatario_nome?: string;
    }
  ): Promise<{ message: string }> {
    const response = await api.post<{
      success: boolean;
      data?: { message: string };
      error?: string;
    }>(`/client/vouchers/meus/${compraId}/transferir`, dados);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao transferir voucher');
  }

  // ============================================
  // HELPERS
  // ============================================

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      ativo: 'Ativo',
      parcialmente_usado: 'Parcialmente Usado',
      usado: 'Usado',
      expirado: 'Expirado',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      ativo: 'success',
      parcialmente_usado: 'warning',
      usado: 'default',
      expirado: 'error',
      cancelado: 'error',
    };
    return colors[status] || 'default';
  }

  formatValor(valor: number | null, tipo: 'valor' | 'desconto', percentagem?: number | null): string {
    if (tipo === 'desconto' && percentagem) {
      return `${percentagem}% OFF`;
    }
    if (valor) {
      return `${valor.toLocaleString('pt-CV')} ECV`;
    }
    return '-';
  }

  // ============================================
  // PORTAL (BUSINESS) - voucher management
  // ============================================

  async portalListVouchers(params?: { status?: string; tipo_id?: number }): Promise<PortalVoucher[]> {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.tipo_id) qs.append('tipo_id', String(params.tipo_id));
    const url = `/portal/vouchers${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: PortalVoucher[] }>(url);
    return res.data.data || [];
  }

  async portalGetVoucher(id: number): Promise<PortalVoucher> {
    const res = await api.get<{ success: boolean; data?: PortalVoucher; error?: string }>(
      `/portal/vouchers/${id}`
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Falha ao carregar voucher');
    }
    return res.data.data;
  }

  async portalCreateVoucher(input: PortalVoucherInput): Promise<PortalVoucher> {
    const res = await api.post<{ success: boolean; data?: PortalVoucher; error?: string }>(
      '/portal/vouchers',
      input
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Falha ao criar voucher');
    }
    return res.data.data;
  }

  async portalUpdateVoucher(id: number, input: Partial<PortalVoucherInput>): Promise<PortalVoucher> {
    const res = await api.put<{ success: boolean; data?: PortalVoucher; error?: string }>(
      `/portal/vouchers/${id}`,
      input
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Falha ao atualizar voucher');
    }
    return res.data.data;
  }

  async portalDeleteVoucher(id: number): Promise<void> {
    const res = await api.delete<{ success: boolean; error?: string }>(`/portal/vouchers/${id}`);
    if (!res.data.success) {
      throw new Error(res.data.error || 'Falha ao eliminar voucher');
    }
  }

  async portalGetTipos(): Promise<VoucherTipo[]> {
    const res = await api.get<{ success: boolean; data?: VoucherTipo[] }>('/portal/vouchers/tipos');
    return res.data.data || [];
  }

  async portalGetStats(): Promise<PortalVoucherStats | null> {
    const res = await api.get<{ success: boolean; data?: PortalVoucherStats }>(
      '/portal/vouchers/stats/summary'
    );
    return res.data.data || null;
  }

  async portalGetVendas(
    voucherId: number,
    params?: { limit?: number; offset?: number }
  ): Promise<PortalVoucherVenda[]> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/portal/vouchers/${voucherId}/vendas${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: PortalVoucherVenda[] }>(url);
    return res.data.data || [];
  }

  async portalValidarVoucher(payload: {
    qr_code_data?: string;
    codigo_unico?: string;
  }): Promise<VoucherValidacao> {
    const res = await api.post<{ success: boolean; data?: VoucherValidacao; error?: string }>(
      '/portal/vouchers/validar',
      payload
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Falha ao validar voucher');
    }
    return res.data.data;
  }

  async portalResgatarVoucher(payload: {
    compra_id: number;
    valor_resgatado?: number | null;
    desconto_aplicado?: number | null;
    notas?: string | null;
    localizacao?: string | null;
    metodo_validacao?: 'qr_code' | 'codigo_manual';
  }): Promise<{ message: string; novo_status: string; valor_restante: number }> {
    const res = await api.post<{
      success: boolean;
      data?: { message: string; novo_status: string; valor_restante: number };
      error?: string;
    }>('/portal/vouchers/resgatar', payload);
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Falha ao resgatar voucher');
    }
    return res.data.data;
  }

  async portalGetResgatesHistorico(params?: {
    limit?: number;
    offset?: number;
  }): Promise<PortalResgateHistorico[]> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/portal/vouchers/resgates/historico${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: PortalResgateHistorico[] }>(url);
    return res.data.data || [];
  }
}

export default new VouchersService();
